import {
  Cross2Icon,
  FileTextIcon,
  ImageIcon,
  UploadIcon,
  VideoIcon,
} from '@radix-ui/react-icons';
import { TextField } from '@radix-ui/themes';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { t } from 'i18next';
import { IFileInput } from 'interfaces/forms/file';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { ImageMime, TextMime, VideoMime } from 'lib_ts/enums/files.enums';
import { useEffect, useMemo, useRef, useState } from 'react';

export const CommonFileInput = (props: IFileInput) => {
  const [files, setFiles] = useState<File[]>([]);

  const fileTypeIcon = useMemo(() => {
    if (!props.acceptedTypes) {
      return <UploadIcon />;
    }

    if (
      ArrayHelper.hasIntersection(props.acceptedTypes, [
        TextMime.CSV,
        TextMime.JSON,
      ])
    ) {
      return <FileTextIcon />;
    }

    if (ArrayHelper.hasIntersection(props.acceptedTypes, [VideoMime.MP4])) {
      return <VideoIcon />;
    }

    if (
      ArrayHelper.hasIntersection(props.acceptedTypes, [
        ImageMime.JPEG,
        ImageMime.JPG,
        ImageMime.PNG,
      ])
    ) {
      return <ImageIcon />;
    }

    return <UploadIcon />;
  }, [props.acceptedTypes]);

  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // notify the parent that files has changed
  useEffect(() => {
    props.onChange(files);
  }, [files]);

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      <form ref={formRef} hidden>
        <input
          type="file"
          ref={inputRef}
          accept={props.acceptedTypes?.join(', ')}
          multiple={props.multiple}
          onChange={(e) => {
            const { files } = e.target;

            if (!files) {
              return;
            }

            // todo: add support for filterFiles

            setFiles([...files]);
          }}
        />
      </form>

      <TextField.Root
        className={props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        color={props.inputColor}
        disabled={props.disabled}
        name={props.name}
        onClick={() => {
          if (props.disabled) {
            return;
          }

          if (!formRef.current) {
            return;
          }

          // so that uploading the same filename repeatedly will work
          formRef.current.reset();

          if (!inputRef.current) {
            return;
          }

          // open the OS's file selector dialog
          inputRef.current.click();
        }}
        placeholder={
          props.placeholder ? t(props.placeholder).toString() : undefined
        }
        // print the filename(s) if any
        value={files.map((f) => f.name).join(', ')}
        // disallow direct input into this field
        readOnly
      >
        <TextField.Slot side="left">{fileTypeIcon}</TextField.Slot>

        {files.length > 0 && (
          <TextField.Slot
            side="right"
            className="cursor-pointer"
            onClick={() => setFiles([])}
          >
            <Cross2Icon />
          </TextField.Slot>
        )}
      </TextField.Root>
    </CommonInputWrapper>
  );
};
